import { useCallback, useEffect, useRef, useState } from 'react';
import { sAbonner } from '@/shared/donnees/demo/magasin';
import { enModeDemo } from '@/shared/supabase/client';
import { surChangementEnDirect } from '@/shared/supabase/enDirect';

export type Lecture<T> =
  { statut: 'chargement' } | { statut: 'erreur'; message: string } | { statut: 'pret'; donnees: T };

/**
 * Lit une donnée au montage, la relit quand `cle` change (l'identifiant de la
 * fiche) ou sur demande (`relire`) après une écriture. Se relit aussi quand les
 * données changent ailleurs : un autre écran, un autre compte (le direct).
 */
export function useDonnees<T>(lire: () => Promise<T>, cle = '') {
  const [lecture, setLecture] = useState<Lecture<T>>({ statut: 'chargement' });
  const [version, setVersion] = useState(0);
  const lecteur = useRef(lire);

  useEffect(() => {
    lecteur.current = lire;
  });

  useEffect(() => {
    let actif = true;
    lecteur
      .current()
      .then((donnees) => {
        if (actif) setLecture({ statut: 'pret', donnees });
      })
      .catch((e: unknown) => {
        if (actif) {
          setLecture({
            statut: 'erreur',
            message: e instanceof Error ? e.message : 'La lecture n’a pas abouti.',
          });
        }
      });
    return () => {
      actif = false;
    };
  }, [cle, version]);

  /* Relire quand les données changent ailleurs : le magasin en accès libre, le
     direct de Supabase sinon. Un onglet caché ne relit pas : il note qu'un
     changement est arrivé et relit une fois revenu au premier plan. */
  useEffect(() => {
    let enRetard = false;
    const relireSiVisible = () => {
      if (document.visibilityState === 'hidden') {
        enRetard = true;
        return;
      }
      setVersion((v) => v + 1);
    };
    const auRetour = () => {
      if (document.visibilityState !== 'visible' || !enRetard) return;
      enRetard = false;
      setVersion((v) => v + 1);
    };
    const desabonner = enModeDemo()
      ? sAbonner(relireSiVisible)
      : surChangementEnDirect(relireSiVisible);
    document.addEventListener('visibilitychange', auRetour);
    return () => {
      desabonner();
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, []);

  const relire = useCallback(() => {
    setVersion((v) => v + 1);
  }, []);

  return { lecture, relire };
}
